"use client";

import { useRef, useState, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslations, useLocale } from "next-intl";
import { useTenantBranding } from "@/app/context/TenantBrandingContext";
import type { LocalizedText } from "@/app/api/utils/types/tenants";

const getDefaultFAQs = (t: ReturnType<typeof useTranslations>, tenantName: string) => [
  {
    question: t("HomePage.FAQsList.1.question"),
    answer: t("HomePage.FAQsList.1.answer"),
  },
  {
    question: t("HomePage.FAQsList.2.question"),
    answer: t("HomePage.FAQsList.2.answer"),
  },
  {
    question: t("HomePage.FAQsList.3.question", { tenantName }),
    answer: t("HomePage.FAQsList.3.answer", { tenantName }),
  },
  {
    question: t("HomePage.FAQsList.4.question"),
    answer: t("HomePage.FAQsList.4.answer"),
  },
];

function getLocalizedText(text: LocalizedText, locale: string): string {
  return text[locale as keyof LocalizedText] || text.en || "";
}

export default function FaqSection() {
  const t = useTranslations();
  const locale = useLocale();
  const { name: tenantName, faqs: tenantFaqs } = useTenantBranding();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs =
    tenantFaqs && tenantFaqs.length > 0
      ? tenantFaqs.map((faq) => ({
          question: getLocalizedText(faq.question, locale),
          answer: getLocalizedText(faq.answer, locale),
        }))
      : getDefaultFAQs(t, tenantName);

  return (
    <div className="container mx-auto">
      <section className="md:py-10 py-6 pb-16">
        <h2 className="text-2xl font-bold md:mb-6 mb-4 font-switzer">
          {t("HomePage.FAQs")}
        </h2>
        <div>
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              question={faq.question}
              answer={faq.answer}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function AccordionItem({
  question,
  answer,
  isOpen,
  onClick,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState("0px");

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setHeight(`${contentRef.current.scrollHeight}px`);
    } else {
      setHeight("0px");
    }
  }, [isOpen]);

  return (
    <div>
      <button
        onClick={onClick}
        className="w-full flex justify-between items-center md:text-2xl rounded-none md:font-medium text-base font-semibold text-black dark:text-white focus:outline-none px-0 font-switzer py-2 border-b border-b-sleap-gray-400 dark:border-b-sleap-gray-600"
      >
        <span>{question}</span>
        <span>
          {isOpen ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </span>
      </button>

      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: height }}
      >
        <p className="my-2 text-sleap-gray-800 dark:text-white md:text-lg text-sm font-switzer dark:opacity-50">
          {answer}
        </p>
      </div>
    </div>
  );
}
